/**
 * DayTabs — accessible horizontal tab navigation for 7 weekdays.
 *
 * Accessibility (WAI-ARIA Tabs pattern):
 *   - Container: role="tablist"
 *   - Each day: role="tab", aria-selected, aria-controls pointing to panel id
 *   - Keyboard: ArrowLeft/ArrowRight cycle; Home→first; End→last; wrap on edges
 *   - Active tab: orange underline AND aria-selected (not color alone)
 *   - Touch targets: min-h-[44px] on every tab button
 *
 * No native dialogs. No dangerouslySetInnerHTML.
 */

import { useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface DayTabsProps {
  /** Index of the currently selected day (0=Lunes … 6=Domingo). */
  activeDay: WeekdayIndex;
  /** Called when the user selects a different day. */
  onDaySelect: (day: WeekdayIndex) => void;
}

// ---------------------------------------------------------------------------
// Day labels
// ---------------------------------------------------------------------------

const DAY_LABELS: readonly string[] = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

const PANEL_ID_PREFIX = "weekly-plan-panel";
const TAB_ID_PREFIX = "weekly-plan-tab";

// ---------------------------------------------------------------------------
// DayTabs
// ---------------------------------------------------------------------------

export function DayTabs({ activeDay, onDaySelect }: DayTabsProps): JSX.Element {
  // Refs to each tab button so keyboard navigation can move DOM focus
  // (roving tabindex requires focus to follow the selected tab).
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Only steal DOM focus when the change came from a keyboard navigation,
  // never on initial mount or pointer clicks.
  const shouldFocusRef = useRef(false);

  useEffect(() => {
    if (!shouldFocusRef.current) return;
    shouldFocusRef.current = false;
    tabRefs.current[activeDay]?.focus();
  }, [activeDay]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const total = DAY_LABELS.length;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      shouldFocusRef.current = true;
      const next = ((activeDay + 1) % total) as WeekdayIndex;
      onDaySelect(next);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      shouldFocusRef.current = true;
      const prev = ((activeDay - 1 + total) % total) as WeekdayIndex;
      onDaySelect(prev);
    } else if (e.key === "Home") {
      e.preventDefault();
      shouldFocusRef.current = true;
      onDaySelect(0);
    } else if (e.key === "End") {
      e.preventDefault();
      shouldFocusRef.current = true;
      onDaySelect(6);
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Días de la semana"
      className="flex gap-1 overflow-x-auto pb-1"
      onKeyDown={handleKeyDown}
    >
      {DAY_LABELS.map((label, i) => {
        const isActive = i === activeDay;
        const idx = i as WeekdayIndex;

        return (
          <button
            key={label}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            id={`${TAB_ID_PREFIX}-${i}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`${PANEL_ID_PREFIX}-${i}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onDaySelect(idx)}
            className={`min-h-[44px] shrink-0 rounded-lg px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${
              isActive
                ? "border-b-2 border-orange-500 bg-zinc-800 text-orange-400"
                : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported constant for panel id prefix (used by DayPlanCard)
// ---------------------------------------------------------------------------

export { PANEL_ID_PREFIX, TAB_ID_PREFIX };
